import { useCallback, useEffect, useRef, useState } from 'react';
import centreVoipService, { VoipToken } from '../services/centreVoip.service';
import { ActiveCallView } from './useWhatsAppCall';
import { unlockCentreNotifSound } from '../utils/centreNotifSound';
import { remoteSipUser, sipTarget } from '../utils/sipCall.utils';

type SipSession = {
  id?: string;
  direction?: string;
  isEstablished?: () => boolean;
  connection?: RTCPeerConnection;
  remote_identity?: { uri?: { user?: string } };
  answer: (opts: Record<string, unknown>) => void;
  terminate: () => void;
  mute: (opts?: { audio?: boolean }) => void;
  unmute: (opts?: { audio?: boolean }) => void;
  hold: () => void;
  unhold: () => void;
  on: (event: string, handler: (data?: any) => void) => void;
};

type SipUA = {
  start: () => void;
  stop: () => void;
  call: (target: string, opts: Record<string, unknown>) => SipSession;
  on: (event: string, handler: (data?: any) => void) => void;
};

interface UseSipCallArgs {
  storeId: number | null;
  enabled: boolean;
  onError?: (message: string) => void;
}

export const useSipCall = ({ storeId, enabled, onError }: UseSipCallArgs) => {
  const [view, setView] = useState<ActiveCallView | null>(null);
  const [muted, setMuted] = useState(false);
  const [held, setHeld] = useState(false);
  const uaRef = useRef<SipUA | null>(null);
  const sessionRef = useRef<SipSession | null>(null);
  const credsRef = useRef<VoipToken | null>(null);
  const callIdRef = useRef<number | null>(null);
  const remoteEl = useRef<HTMLAudioElement | null>(null);
  const readyRef = useRef(false);
  const connectingRef = useRef<Promise<SipUA | null> | null>(null);

  const audioEl = useCallback(() => {
    if (!remoteEl.current) {
      const el = new Audio();
      el.autoplay = true;
      el.setAttribute('playsinline', 'true');
      el.volume = 1;
      remoteEl.current = el;
    }
    return remoteEl.current;
  }, []);

  const unlockMic = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) return;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((track) => track.stop());
  }, []);

  const reset = useCallback(() => {
    sessionRef.current = null;
    callIdRef.current = null;
    setMuted(false);
    setHeld(false);
    setView(null);
    if (remoteEl.current) remoteEl.current.srcObject = null;
  }, []);

  const bindSession = useCallback((session: SipSession) => {
    sessionRef.current = session;
    const el = audioEl();
    const attach = () => {
      const pc = session.connection;
      if (!pc) return;
      pc.ontrack = (event) => {
        el.srcObject = event.streams[0] || new MediaStream([event.track]);
        void el.play().catch(() => {});
      };
    };
    session.on('peerconnection', attach);
    attach();
    session.on('accepted', () => setView((prev) => (prev ? { ...prev, phase: 'active' } : prev)));
    session.on('confirmed', () => {
      setView((prev) => (prev ? { ...prev, phase: 'active' } : prev));
      const id = callIdRef.current;
      if (id && storeId) void centreVoipService.event(storeId, id, { state: 'active' });
    });
    session.on('ended', () => {
      const id = callIdRef.current;
      const sid = storeId;
      reset();
      if (id && sid) void centreVoipService.hangup(sid, id).catch(() => {});
    });
    session.on('failed', () => {
      const id = callIdRef.current;
      const sid = storeId;
      reset();
      if (id && sid) void centreVoipService.hangup(sid, id).catch(() => {});
    });
  }, [audioEl, reset, storeId]);

  const dropUa = useCallback(() => {
    try {
      uaRef.current?.stop();
    } catch {
      /* closed */
    }
    uaRef.current = null;
    readyRef.current = false;
    connectingRef.current = null;
    credsRef.current = null;
  }, []);

  const ensureUa = useCallback(async (): Promise<SipUA | null> => {
    if (!enabled || !storeId) return null;
    if (uaRef.current && readyRef.current) return uaRef.current;
    if (connectingRef.current) return connectingRef.current;

    connectingRef.current = (async () => {
      const creds = await centreVoipService.getToken(storeId);
      if (creds.provider !== 'sip' || !creds.websocket_url || !creds.sip_uri || !creds.sip_password) {
        throw new Error('Ligne SIP indisponible');
      }
      credsRef.current = creds;
      await import('webrtc-adapter');
      const JsSIP = (await import('jssip')).default;
      const socket = new JsSIP.WebSocketInterface(creds.websocket_url);
      const ua = new JsSIP.UA({
        sockets: [socket],
        uri: creds.sip_uri,
        password: creds.sip_password,
        display_name: creds.caller_id,
        register: true,
        session_timers: false
      }) as SipUA;

      ua.on('newRTCSession', (data: { originator?: string; session?: SipSession; request?: { call_id?: string } }) => {
        if (data?.originator !== 'remote' || !data.session) return;
        if (sessionRef.current) {
          try {
            data.session.terminate();
          } catch {
            /* busy */
          }
          return;
        }
        bindSession(data.session);
        const phone = remoteSipUser(data.session) || 'inconnu';
        setView({
          id: 0,
          phone,
          name: phone,
          direction: 'in',
          phase: 'incoming',
          provider: 'voip'
        });
        if (!storeId) return;
        void centreVoipService.reportInbound(storeId, {
          phone,
          sip_call_id: String(data.request?.call_id || data.session.id || '')
        }).then((call) => {
          callIdRef.current = call.id;
          setView((prev) => (prev ? {
            ...prev,
            id: call.id,
            phone: call.phone || prev.phone,
            name: call.contact_name || prev.name
          } : prev));
        }).catch(() => {});
      });

      await new Promise<void>((resolve, reject) => {
        let settled = false;
        let failed = false;
        const timer = window.setTimeout(() => reject(new Error('Connexion SIP expirée')), 12000);
        const ok = () => {
          if (settled || failed) return;
          settled = true;
          window.clearTimeout(timer);
          readyRef.current = true;
          resolve();
        };
        ua.on('registered', ok);
        ua.on('connected', () => {
          window.setTimeout(ok, 1500);
        });
        ua.on('registrationFailed', (data: { cause?: string }) => {
          failed = true;
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          reject(new Error(data?.cause || 'Enregistrement SIP refusé'));
        });
        ua.start();
      });

      uaRef.current = ua;
      return ua;
    })().catch((error) => {
      dropUa();
      throw error;
    }).finally(() => {
      connectingRef.current = null;
    });

    return connectingRef.current;
  }, [bindSession, dropUa, enabled, storeId]);

  useEffect(() => {
    if (!enabled || !storeId) {
      dropUa();
      return undefined;
    }
    void ensureUa().catch((error) => {
      onError?.(error.message || 'SIP indisponible');
    });
    return undefined;
  }, [dropUa, enabled, ensureUa, onError, storeId]);

  useEffect(() => () => {
    dropUa();
  }, [dropUa]);

  const startCall = useCallback(async ({
    phone,
    name,
    orderId
  }: { phone: string; name?: string; orderId?: number | null }) => {
    if (!storeId || !phone) return;
    unlockCentreNotifSound();
    try {
      await unlockMic();
      const ua = await ensureUa();
      const creds = credsRef.current;
      if (!ua || !creds?.sip_domain) throw new Error('Voix indisponible');
      setView({
        id: 0,
        phone,
        name: name || phone,
        direction: 'out',
        phase: 'outgoing',
        provider: 'voip'
      });
      const started = await centreVoipService.startCall(storeId, {
        phone,
        contact_name: name,
        order_id: orderId
      });
      callIdRef.current = started.call.id;
      setView({
        id: started.call.id,
        phone: started.call.phone,
        name: started.call.contact_name || name || phone,
        direction: 'out',
        phase: 'outgoing',
        provider: 'voip'
      });
      const session = ua.call(sipTarget(started.call.phone, creds.sip_domain), {
        mediaConstraints: { audio: true, video: false },
        pcConfig: { iceServers: creds.ice_servers || [] },
        extraHeaders: creds.caller_id
          ? [`P-Preferred-Identity: <sip:${creds.caller_id}@${creds.sip_domain}>`]
          : []
      });
      bindSession(session);
    } catch (error: any) {
      reset();
      onError?.(error?.response?.data?.error?.message || error.message || 'Appel impossible');
    }
  }, [bindSession, ensureUa, onError, reset, storeId, unlockMic]);

  const accept = useCallback(async () => {
    if (!storeId || !sessionRef.current) return;
    unlockCentreNotifSound();
    setView((prev) => (prev ? { ...prev, phase: 'connecting' } : prev));
    try {
      await unlockMic();
      const creds = credsRef.current;
      sessionRef.current.answer({
        mediaConstraints: { audio: true, video: false },
        pcConfig: { iceServers: creds?.ice_servers || [] }
      });
      if (callIdRef.current) {
        await centreVoipService.accept(storeId, callIdRef.current);
      }
    } catch (error: any) {
      reset();
      onError?.(error?.response?.data?.error?.message || error.message || 'Décrochage impossible');
    }
  }, [onError, reset, storeId, unlockMic]);

  const reject = useCallback(async () => {
    const id = callIdRef.current;
    const sid = storeId;
    try {
      sessionRef.current?.terminate();
    } catch {
      /* closed */
    }
    reset();
    if (id && sid) {
      try {
        await centreVoipService.reject(sid, id);
      } catch {
        /* already closed */
      }
    }
  }, [reset, storeId]);

  const hangup = useCallback(async () => {
    const id = callIdRef.current;
    const sid = storeId;
    try {
      sessionRef.current?.terminate();
    } catch {
      /* closed */
    }
    reset();
    if (id && sid) {
      try {
        await centreVoipService.hangup(sid, id);
      } catch {
        /* already closed */
      }
    }
  }, [reset, storeId]);

  const toggleMute = useCallback(() => {
    const session = sessionRef.current;
    if (!session) return;
    if (muted) session.unmute({ audio: true });
    else session.mute({ audio: true });
    setMuted(!muted);
  }, [muted]);

  const toggleHold = useCallback(() => {
    const session = sessionRef.current;
    if (!session) return;
    if (held) session.unhold();
    else session.hold();
    setHeld(!held);
    setView((prev) => (prev ? { ...prev, held: !held } : prev));
  }, [held]);

  return {
    view,
    muted,
    held,
    startCall,
    accept,
    reject,
    hangup,
    toggleMute,
    toggleHold
  };
};
